import { Component, computed, HostListener, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { EmptyData } from "../../../../../utils/empty-data/empty-data";
import { EgpPipePipe } from "../../../../../shared/ui/shared-tables/egpPipe/egp-pipe-pipe";
import { ActionInterface } from '../../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { ActionIcon } from "../../../../../shared/ui/icons/action-icon/action-icon";
import { PharmaciesIcon } from "../../../../../shared/ui/icons/pharmacies-icon/pharmacies-icon";
import { PharmacyIcon } from "../../../../../shared/ui/icons/pharmacy-icon/pharmacy-icon";
import { PeopleIcon } from "../../../../../shared/ui/icons/people-icon/people-icon";

export type ScopeResolver = (action: ActionInterface) => number | null;

export interface CardHeaderItem {
  key: string;
  labelAr: string;
  labelEn: string;
  type?: 'text' | 'status' | 'date' | 'currency' | 'actions';
  position?: 'header' | 'body' | 'footer';
  scope?: number;
}

@Component({
  selector: 'app-cards-list',
  standalone: true,
  imports: [EmptyData, EgpPipePipe, ActionIcon, DatePipe, PharmaciesIcon, PharmacyIcon, PeopleIcon],
  templateUrl: './card-list.html',
  styleUrl: './card-list.scss',
})
export class CardList {
  private locale = inject(LOCALE_ID);
  isArabic = computed(() => this.locale.startsWith('ar'));

 headerItems = input.required<CardHeaderItem[]>();
  rows = input.required<Record<string, any>[]>();
  currentActions = input<ActionInterface[]>();
  emptyFilterTitle = input<string>();
  emptyContentTitle = input<string>();
  emptyContentDescription = input<string>();
  isFiltersApplied = input<boolean>(false);
  scopeResolver = input<ScopeResolver>(() => null);

  actionTrigger = output<{ event: Event; actionId: number; entity: any }>();
  toggleDropdown = output<{ id?: any | '_'; event?: any }>();

  activeDropdownId = signal<any>(null);

 toggleRow(id: any) {
    if (id) {
      this.toggleDropdown.emit(id.entityId);
      this.activeDropdownId.set(id.entityId);
    } else {
      this.activeDropdownId.set(null);
    }
  }

  onHandleAction(event: Event, actionId: number, entity: any) {
    this.actionTrigger.emit({ event, actionId, entity });
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.activeDropdownId.set(null);
  }

  getHeader(key: string): CardHeaderItem | undefined {
  return this.headerItems().find(item => item.key === key);
}

getLabel(item: CardHeaderItem): string {
  return this.isArabic() ? item.labelAr : item.labelEn;
}
getValue(row: Record<string, any>, item: CardHeaderItem): any {
  return row[item.key];
}

formatValue(value: any, item: CardHeaderItem): string {

  if (value === null || value === undefined) {
    return '';
  }

  if (item.type === 'status') {
    return this.isArabic()
      ? value?.titleAr || value
      : value?.titleEn || value;
  }

  return String(value);
}

getStatusClass(status: string): string {
  if (!status) return 'bg-gray-100 text-gray-600';
  const s = status.trim().toLowerCase();

  switch (s) {
    // --- الحالات القديمة الخاصة بك (تم الاحتفاظ بها كما هي) ---
    case 'paid':
      return 'bg-emerald-100 text-emerald-700';

    case 'partially paid':
      return 'bg-amber-100 text-amber-700';

    case 'pending':
    case 'pending payment':
      return 'bg-gray-100 text-gray-600';

    // --- حالات المرتجعات (باللغتين العربية والإنجليزية) ---

    // قيد الانتظار / قيد المراجعة (Pending / Pending Review) -> أصفر / أورانج فاتح
    case 'pending_review':
    case 'pending review':
    case 'قيد المراجعة':
    case 'قيد الانتظار':
      return 'bg-amber-100 text-amber-700';

    // يحتاج إلى تعديل (Modification Requested) -> أورانج / بني فاتح
    case 'modification_requested':
    case 'modification requested':
    case 'يحتاج إلى تعديل':
    case 'يحتاج لتعديل':
      return 'bg-orange-100 text-orange-700';

    // مقبول / تم التوصيل (Accepted / Delivered) -> أخضر
    case 'accepted':
    case 'مقبول':
    case 'delivered':
    case 'تم التوصيل':
      return 'bg-emerald-100 text-emerald-700';

    // تم التوصيل جزئياً -> أصفر
    case 'partially delivered':
    case 'تم التوصيل جزئياً':
    case 'تم التوصيل جزئيا':
      return 'bg-amber-100 text-amber-700';

    // تم استلام الموزع (Picked up) -> أزرق فاتح
    case 'picked_up':
    case 'picked up':
    case 'تم استلام الموزع':
      return 'bg-blue-100 text-blue-700';

    // تمت الطباعة -> أزرق فاتح جداً
    case 'printed':
    case 'تمت الطباعة':
      return 'bg-sky-100 text-sky-700';

    // تم استلام المخزون (Products Returns Received) -> بنفسجي / أزرق داكن
    case 'products_returns_received':
    case 'products returns received':
    case 'تم استلام المخزون':
      return 'bg-indigo-100 text-indigo-700';

    // مكتمل / مغلق (Closed / Completed) -> رمادي داكن أو أخضر فاتح
    case 'closed':
    case 'completed':
    case 'مكتمل':
    case 'مغلق':
      return 'bg-slate-100 text-slate-700';

    // ملغي / لم يتم التوصيل (Cancelled / Not Delivered) -> أحمر
    case 'cancelled':
    case 'canceled':
    case 'ملغي':
    case 'not delivered':
    case 'لم يتم التوصيل':
      return 'bg-rose-100 text-rose-700';

    default:
      return 'bg-amber-100 text-amber-700';
  }
}
}